/*global swal*/
import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import uniqueId from 'lodash.uniqueid';
import SweetAlert from 'react-swal';
import useAxios from 'axios-hooks';
import { differenceInMinutes } from 'date-fns';

import { EVISIT_STATUS } from '../../../../config/constants';
import { Button } from '../../../../components';
import { fullname } from '../../common/formatting/provider';
import ChatBubble from './ChatBubble';

const scrollIntoViewOptions = {
	behavior: 'smooth',
	block: 'nearest',
};

const Chat = ({
	evisit,
	interactive,
	appSocket,
	user,
	onEvisitClosed,
	onGoToVideoChat,
	isPatientConnected,
	setPatientConnected,
	patient,
}) => {
	const [isConnected, setIsConnected] = useState(appSocket.connected);
	const [isRemoteTyping, setIsRemoteTyping] = useState(false);
	const [chatMessage, setChatMessage] = useState('');
	const [messages, setMessages] = useState([]);
	const [showSwal, setShowSwal] = useState(false);
	const remoteTypingTimeout = useRef(null);
	const localTypingTimeout = useRef(null);
	const scrollChatRef = useRef(null);

	const [{ data: chatMessages, loading: loadingMessages /* , error */ }] =
		useAxios({
			method: 'GET',
			url: `/evisits/${evisit.id}/evisit_chats`,
			params: {
				filter: JSON.stringify({
					limit: interactive ? 500 : null,
					where: {
						and: [
							{ text: { neq: 'video_requested' } },
							{ text: { neq: 'video_started' } },
							{ text: { neq: 'video_ended' } },
							{ text: { neq: 'video_declined' } },
							{ text: { neq: 'photos_uploaded' } },
						],
					},
					order: ['sent_dttm DESC'],
				}),
			},
		});
	const [
		{ data: chatParticipants, loading: loadingParticipants /* , error */ },
	] = useAxios({
		method: 'GET',
		url: `/evisits/${evisit.id}/chat-participants`,
	});

	const chatData = {
		participantType: 'provider',
		participantId: user.get('id'),
		chatRoom: evisit.chatRoom,
		evisitId: evisit.id,
		participantName: fullname(user.toJSON()),
	};

	useEffect(() => {
		const userIsTyping = (data) => {
			if (data.participantType !== 'provider') {
				if (data.isTyping) {
					remoteTypingTimeout.current = setTimeout(() => {
						setIsRemoteTyping(false);
					}, 5000);
					setIsRemoteTyping(true);
				} else {
					setIsRemoteTyping(false);
				}
			}
		};

		setPatientConnected(false);
		appSocket.on('connect', () => {
			setIsConnected(true);
		});
		appSocket.on('disconnect', () => {
			setIsConnected(false);
		});

		appSocket.on('reconnect', () => {
			setIsRemoteTyping(false);
			appSocket.emit('evisitChatJoin', chatData);
		});

		if (interactive) {
			appSocket.emit('evisitChatJoin', chatData, (response) => {
				setPatientConnected(response.isPatientInRoom);
			});
			appSocket.on(
				'chat message',
				(participantType, participantId, participantName, msg) => {
					const [, action] = participantType.split(' ');
					if ((participantId || action) && msg) {
						const message = {
							id: uniqueId('local-'),
							text: msg,
							participant_type: participantType,
							participantName: participantName,
							participantId: participantId,
							sent_dttm: new Date(),
							chatRoom: chatData.chatRoom,
							evisitId: evisit.id,
							provider: user.toJSON(),
							patient,
						};
						setMessages((currMessages) => {
							const updatedMessages = [...currMessages];
							updatedMessages.push(message);
							return updatedMessages;
						});
					}
					// if (scrollChatRef.current) {
					// 	scrollChatRef.current.scrollIntoView(scrollIntoViewOptions);
					// }
				}
			);
			appSocket.on('isTyping', userIsTyping);
			appSocket.on('evisit closed', onEvisitClosed);
			appSocket.on('patient joined', () => {
				setPatientConnected(true);
			});
			appSocket.on('patient left', () => {
				setPatientConnected(false);
			});
			// if (scrollChatRef.current) {
			// 	scrollChatRef.current.scrollIntoView(scrollIntoViewOptions);
			// }
		}

		return function cleanUp() {
			appSocket.off('connect');
			appSocket.off('disconnect');

			if (interactive) {
				appSocket.emit('evisitChatLeave', { chatRoom: evisit.chatRoom });
			}

			if (appSocket) {
				appSocket.removeAllListeners('chat message');
				appSocket.removeAllListeners('isTyping');
				appSocket.removeAllListeners('evisit closed');
				appSocket.removeAllListeners('patient joined');
				appSocket.removeAllListeners('patient left');
				appSocket.off('reconnect');
			}

			if (remoteTypingTimeout && remoteTypingTimeout.current) {
				clearTimeout(remoteTypingTimeout.current);
			}
		};
	}, []);

	useEffect(() => {
		if (
			Array.isArray(chatMessages) &&
			chatParticipants &&
			Array.isArray(chatParticipants.cb)
		) {
			// prep messages
			const preppedMessages = chatMessages
				.map((message) => ({
					...message,
					patient,
					provider:
						message.participant_type === 'provider'
							? chatParticipants.cb.find(
									({ id }) => message.participantId === id
							  )
							: null,
					sent_dttm: new Date(message.sent_dttm),
				}))
				.sort((model1, model2) => {
					if (model1.sent_dttm < model2.sent_dttm) {
						return -1;
					} else if (model1.sent_dttm === model2.sent_dttm) {
						return 0;
					} else {
						return 1;
					}
				});
			setMessages(preppedMessages);
		}
	}, [chatMessages, chatParticipants]);

	useEffect(() => {
		if (messages.length > 0 && scrollChatRef.current) {
			scrollChatRef.current.scrollIntoView(scrollIntoViewOptions);
		}
	}, [messages]);

	useEffect(() => {
		if (localTypingTimeout && localTypingTimeout.current) {
			clearTimeout(localTypingTimeout.current);
		}
		if (chatMessage === '') {
			appSocket.emit('typing', {
				...chatData,
				typing: false,
			});
		} else {
			appSocket.emit('typing', {
				...chatData,
				typing: true,
			});
			localTypingTimeout.current = setTimeout(() => {
				appSocket.emit('typing', {
					...chatData,
					typing: false,
				});
			}, 3000);
		}

		return () => {
			if (localTypingTimeout && localTypingTimeout.current) {
				clearTimeout(localTypingTimeout.current);
			}
		};
	}, [chatMessage]);

	const setChatStatus = ({ which }) => {
		if (which === 13) {
			setChatMessage('');
			return false;
		}
	};

	const sendMessage = ({ which }) => {
		if (which === 13) {
			appSocket.emit('chat message', chatMessage);
			clearTimeout(localTypingTimeout.current);
			appSocket.emit('typing', {
				...chatData,
				typing: false,
			});
		}
	};

	const handleCallPatient = () => {
		if (
			evisit.provider_visit_type &&
			evisit.provider_visit_type.instant_visit_ind &&
			!isPatientConnected
		) {
			setShowSwal(true);
		} else {
			onGoToVideoChat(chatData);
		}
	};

	const handleConfirmCallPatient = (isConfirm) => {
		if (isConfirm) {
			onGoToVideoChat(chatData);
		}
		setShowSwal(false);
		swal.close();
	};

	const isClosed = !interactive; // Boolean(evisit.get('end_dttm'));
	const isOpen = evisit.evisit_status === EVISIT_STATUS.IN_PROGRESS;
	const patientName = patient.name_first + ' ' + patient.name_last;

	return (
		<div className="aside-inner">
			<div className="sidebar chat-sidebar">
				<div className="top-bar">
					<div className="chat-name clearfix">
						{!isClosed && isOpen && (
							<div className="pull-right mr-xl">
								<Button
									className="btn-default btn-sm"
									title="Start Video"
									onClick={handleCallPatient}
								/>
							</div>
						)}
						Patient Chat
					</div>
				</div>
				<div className="module">
					<div className="conversation">
						<ol>
							{!isClosed && !isOpen && (
								<div style={{ margin: '10px' }}>
									Chat messages will appear here after the visit has started.
								</div>
							)}
							{(loadingMessages || loadingParticipants) && (
								<div className="m-lg text-center">
									<span className="fa fa-spin fa-circle-o-notch" />
								</div>
							)}
							{messages.map((message, messageIndex) => {
								const showFrom =
									messageIndex > 0 &&
									message.participantId !== 0 &&
									!(
										messages[messageIndex - 1].participant_type ===
											message.participant_type &&
										messages[messageIndex - 1].participantId ===
											message.participantId
									);
								let showTimestamp = true;
								if (messageIndex < messages.length - 1) {
									showTimestamp = !(
										differenceInMinutes(
											new Date(message.sent_dttm),
											new Date(messages[messageIndex + 1].sent_dttm)
										) > 0 ||
										(messages[messageIndex + 1].participant_type ===
											message.participant_type &&
											messages[messageIndex + 1].participantId ===
												message.participantId)
									);
								}
								return (
									<ChatBubble
										message={message}
										key={`${message.id}`}
										showFrom={showFrom}
										showTimestamp={showTimestamp}
									/>
								);
							})}
							<li ref={scrollChatRef} />
						</ol>
						<div className={cn('updates', { hidden: !isRemoteTyping })}>
							{patientName} is typing
						</div>
					</div>
					{isOpen && (
						<form className="chat-form">
							<textarea
								placeholder="Type message and press Enter to send"
								className={cn('chat-box', { hidden: !interactive })}
								disabled={
									!isConnected || loadingMessages || loadingParticipants
								}
								onKeyUp={setChatStatus}
								onKeyPress={sendMessage}
								value={chatMessage}
								onChange={({ target }) => setChatMessage(target.value)}
							/>
						</form>
					)}
				</div>
			</div>
			<SweetAlert
				isOpen={showSwal}
				type="warning"
				title="Patient Offline"
				text="Patient appears to be offline. Clicking Yes will notify the patient that you're starting a video chat. Do you still want to start a video chat?"
				showCancelButton
				confirmButtonText="Yes"
				cancelButtonText="No"
				closeOnConfirm={false}
				closeOnCancel={false}
				callback={handleConfirmCallPatient}
			/>
		</div>
	);
};

Chat.propTypes = {
	evisit: PropTypes.object,
	interactive: PropTypes.bool,
	appSocket: PropTypes.object.isRequired,
	user: PropTypes.object,
	onEvisitClosed: PropTypes.func.isRequired,
	onGoToVideoChat: PropTypes.func.isRequired,
	isPatientConnected: PropTypes.bool,
	setPatientConnected: PropTypes.func.isRequired,
	patient: PropTypes.object.isRequired,
};

export default Chat;
